import { Voicing, AudioSettings, TimeSignature, ParsedChord } from '../../types';
import { getAudioContext, getEditorMasterGain } from './audioContext';
import { NOTES, noteToFrequency, midiToFrequency } from './noteUtils';

interface PlaybackOptions extends AudioSettings {
  duration: number;
  startTime: number;
}

const getVoicedFrequencies = (parsedChord: ParsedChord, voicing: Voicing): number[] => {
  const { notes, bassNote } = parsedChord;
  if (!notes) return [];

  let freqs: number[];
  const effectiveRoot = bassNote || notes[0];

  if (voicing === 'Spread' && notes.length > 1) {
    const otherNotes = [...notes];

    const bassIdx = otherNotes.indexOf(effectiveRoot);
    if (bassIdx > -1) {
      otherNotes.splice(bassIdx, 1);
    }

    const rootFreq = noteToFrequency(effectiveRoot, 3);
    let lastNoteMidi = NOTES[effectiveRoot] + (12 * 3);

    const voicedFreqs = [rootFreq];
    
    otherNotes.forEach(note => {
      let currentNoteMidi = NOTES[note] + (12 * 4);
      while (currentNoteMidi <= lastNoteMidi) {
        currentNoteMidi += 12;
      }
      const freq = 440 * Math.pow(2, (currentNoteMidi - 69) / 12);
      voicedFreqs.push(freq);
      lastNoteMidi = currentNoteMidi;
    });
    freqs = voicedFreqs;

  } else { // Stacked voicing
    freqs = notes.map((note, i) => {
      const octave = (i > 0 && NOTES[note] < NOTES[notes[0]]) ? 5 : 4;
      return noteToFrequency(note, octave);
    });
  }
  
  if (bassNote) {
    const bassFreq = noteToFrequency(bassNote, 2);
    const currentLowest = Math.min(...freqs);
    if (bassFreq < currentLowest) {
      const rootFreq = noteToFrequency(notes[0], (NOTES[notes[0]] < NOTES[notes[0]]) ? 5: 4);
      const rootIndex = freqs.indexOf(rootFreq);
      if (rootIndex > -1) freqs.splice(rootIndex, 1);
      freqs.unshift(bassFreq);
    }
  }
  
  return freqs.sort((a,b) => a - b);
};

const createSoundSource = (ctx: AudioContext, freq: number, settings: AudioSettings): { source: AudioNode[], gain: GainNode } => {
  const gainNode = ctx.createGain();
  let sources: AudioNode[] = [];

  if (settings.timbre === 'Synth') {
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    
    osc.type = settings.synth.waveform;
    osc.frequency.value = freq;

    filter.type = settings.synth.filterType;
    filter.frequency.value = settings.synth.filterCutoff;
    filter.Q.value = settings.synth.filterResonance;

    osc.connect(filter);
    filter.connect(gainNode);
    sources.push(osc);
  } else if (settings.timbre === 'Piano') {
    const partials = [1, 0.5, 0.2, 0.1, 0.05];
    partials.forEach((gain, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * (i + 1);
      
      const partialGain = ctx.createGain();
      partialGain.gain.value = gain * 0.5;
      
      osc.connect(partialGain);
      partialGain.connect(gainNode);
      sources.push(osc);
    });
  } else { // Sine wave
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gainNode);
    sources.push(osc);
  }
  
  gainNode.connect(getEditorMasterGain());
  return { source: sources, gain: gainNode };
};

export const playNotes = (parsedChord: ParsedChord, options: PlaybackOptions) => {
  const ctx = getAudioContext();
  if (!ctx || !parsedChord.notes) return;
  const { duration, timbre, voicing, dynamics, startTime, synth } = options;

  const frequencies = getVoicedFrequencies(parsedChord, voicing);

  frequencies.forEach((freq, i) => {
    const { source: sources, gain: gainNode } = createSoundSource(ctx, freq, options);
    const noteStartTime = startTime + (dynamics === 'Arpeggiated' ? i * 0.05 : 0);
    
    if (timbre === 'Synth') {
      const { attack, decay, sustain, release } = synth;
      const peakGain = 0.25 / frequencies.length;
      const sustainGain = peakGain * sustain;
      const releaseStartTime = noteStartTime + duration - release;

      gainNode.gain.setValueAtTime(0, noteStartTime);
      gainNode.gain.linearRampToValueAtTime(peakGain, noteStartTime + attack);
      gainNode.gain.linearRampToValueAtTime(sustainGain, noteStartTime + attack + decay);
      
      gainNode.gain.setValueAtTime(sustainGain, releaseStartTime > (noteStartTime + attack + decay) ? releaseStartTime : (noteStartTime + attack + decay));
      gainNode.gain.linearRampToValueAtTime(0, releaseStartTime + release);

      const stopTime = releaseStartTime + release;
      sources.forEach(sourceNode => {
        (sourceNode as OscillatorNode).start(noteStartTime);
        (sourceNode as OscillatorNode).stop(stopTime);
      });
    } else {
      gainNode.gain.setValueAtTime(0, noteStartTime);
      gainNode.gain.linearRampToValueAtTime(0.3 / frequencies.length, noteStartTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, noteStartTime + duration);

      sources.forEach(sourceNode => {
        (sourceNode as OscillatorNode).start(noteStartTime);
        (sourceNode as OscillatorNode).stop(noteStartTime + duration);
      });
    }
  });
};

export const playMetronomeClick = (options: { time: number, beat: number, timeSignature: TimeSignature }) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const { time, beat, timeSignature: _timeSignature } = options;
  
  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();
  osc.connect(gainNode);
  gainNode.connect(getEditorMasterGain());

  const isDownbeat = beat === 1;
  osc.frequency.value = isDownbeat ? 880 : 440;
  gainNode.gain.setValueAtTime(0.5, time);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);

  osc.start(time);
  osc.stop(time + 0.05);
};

// --- Live MIDI Playback Functions ---

export interface ActiveSound {
  source: AudioNode[];
  gain: GainNode;
}

export const startLiveNote = (midiNote: number, velocity: number, settings: AudioSettings): ActiveSound | null => {
  const ctx = getAudioContext();
  if (!ctx) return null;

  const freq = midiToFrequency(midiNote);
  const { source, gain: gainNode } = createSoundSource(ctx, freq, settings);
  const velocityGain = velocity / 127;
  
  if (settings.timbre === 'Synth') {
    const { attack, decay, sustain } = settings.synth;
    const peakGain = 0.5 * velocityGain;
    const sustainGain = peakGain * sustain;

    gainNode.gain.cancelScheduledValues(ctx.currentTime);
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(peakGain, ctx.currentTime + attack);
    gainNode.gain.linearRampToValueAtTime(sustainGain, ctx.currentTime + attack + decay);

    source.forEach(sourceNode => (sourceNode as OscillatorNode).start(ctx.currentTime));
  } else { // Piano or Sine
    const peakGain = 0.5 * velocityGain;
    gainNode.gain.cancelScheduledValues(ctx.currentTime);
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(peakGain, ctx.currentTime + 0.01);
    
    source.forEach(sourceNode => (sourceNode as OscillatorNode).start(ctx.currentTime));
  }
  
  return { source, gain: gainNode };
};

export const stopLiveNote = (sound: ActiveSound, settings: AudioSettings) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const { source, gain: gainNode } = sound;

  if (settings.timbre === 'Synth') {
    const { release } = settings.synth;
    const stopTime = ctx.currentTime + release;
    gainNode.gain.cancelScheduledValues(ctx.currentTime);
    gainNode.gain.setValueAtTime(gainNode.gain.value, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, stopTime);
    source.forEach(sourceNode => (sourceNode as OscillatorNode).stop(stopTime));
  } else { // Piano or Sine
    const releaseTime = 0.5;
    const stopTime = ctx.currentTime + releaseTime;
    gainNode.gain.cancelScheduledValues(ctx.currentTime);
    gainNode.gain.setValueAtTime(gainNode.gain.value, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, stopTime);
    source.forEach(sourceNode => (sourceNode as OscillatorNode).stop(stopTime));
  }
};
